import React, { useCallback, useEffect, useState } from 'react';
import { styled } from 'baseui';
import { Banner, KIND } from 'baseui/banner';
import { Button, KIND as BTN_KIND, SHAPE, SIZE } from 'baseui/button';
import { StyledLink } from 'baseui/link';
import { HeadingLarge, ParagraphMedium } from 'baseui/typography';
import { BASE_API_URL } from './consts/base-url';
import { CUI_URLS } from './consts/cui';
import customFetch from './utils/fetch';
function useAuth() {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [waitingForLogin, setWaitingForLogin] = useState<boolean>(false);
  useEffect(() => {
    if (waitingForLogin) {
      return;
    }
    const controller = new AbortController();
    customFetch(`${BASE_API_URL}/whoami`, {
      method: 'GET',
      signal: controller.signal
    }).then(res => {
      if (res.status === 401) {
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(true);
      }
    }).catch(error => {
      setIsLoggedIn(false);
      // TODO: Handle errors without changing login state
      console.error('Error checking auth status:', error);
    });
    return () => {
      controller.abort();
    };
  }, [waitingForLogin]);
  const handleLogin = useCallback(() => {
    if (waitingForLogin) {
      return;
    }
    setWaitingForLogin(true);
    let loginWindow = window.open(`${BASE_API_URL}/login?autocloseIn=5`, '_blank');
    const interval = setInterval(() => {
      if (loginWindow?.closed) {
        setWaitingForLogin(false);
        clearInterval(interval);
      }
    }, 500);
  }, [waitingForLogin]);
  return {
    isLoggedIn,
    handleLogin,
    waitingForLogin
  };
}
const LoginContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  backgroundColor: $theme.colors.backgroundPrimary,
  padding: $theme.sizing.scale800
}));
const MessageContainer = styled('div', ({
  $theme
}) => ({
  marginBottom: $theme.sizing.scale800,
  textAlign: 'center',
  maxWidth: '500px'
}));
export const AuthCheck = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    isLoggedIn,
    handleLogin,
    waitingForLogin
  } = useAuth();
  if (!isLoggedIn) {
    return <LoginContainer>
        <MessageContainer>
          <HeadingLarge>Welcome to uLabel</HeadingLarge>
          <Banner kind={KIND.negative}>
            The system you are accessing contains{' '}
            <StyledLink href={CUI_URLS.CUI_ABOUT}>Controlled Unclassified Information</StyledLink>.
            Use of this system indicates consent to monitoring and recording. All individuals
            viewing, reproducing or disposing of this information are required to protect it in
            accordance with <StyledLink href={CUI_URLS.CFR_PART_2002}>32 CFR Part 2002</StyledLink>{' '}
            and{' '}
            <StyledLink href={CUI_URLS.GSA_ORDER_CIO_2103_2}>
              GSA Order CIO 2103.2 CUI Policy
            </StyledLink>
            .
          </Banner>
          <ParagraphMedium>
            Please log in to continue. Authentication is required to access and work with labeling
            tasks.
          </ParagraphMedium>
        </MessageContainer>
        <Button size={SIZE.large} onClick={handleLogin} shape={SHAPE.default} kind={BTN_KIND.primary} data-testid="login-btn">
          {waitingForLogin ? 'Waiting for login...' : 'Login'}
        </Button>
      </LoginContainer>;
  }
  return <>{children}</>;
};